import { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogClose,
} from "./ui/dialog";
import { KeyRound, Plus, Trash2, Mail, Eye } from "lucide-react";
import { ResendInviteButton, LinkEmployeeControl } from "./LoginTools";
import { PasswordField } from "./PasswordField";
import { useAuth } from "../context/AuthContext";
import { useRoles } from "../lib/useRoles";
import { useSort, SortBtn } from "../lib/sortable";

const CU_A = { name: (u) => u.name || "", role: (u) => u.client_role || "admin" };


export default function ClientLogins({ companyId }) {
  const { user } = useAuth();
  const CLIENT_ROLES = useRoles().client.map((r) => ({ v: r.key, label: r.label }));
  const isAdmin = user?.role === "super_admin";
  const isMaster = isAdmin && (user?.staff_role || "master") === "master";
  const [loginHist, setLoginHist] = useState(null);
  const [histOpen, setHistOpen] = useState(false);
  const lastLogin = (uid) => (loginHist?.users || []).find((x) => x.user_id === uid)?.last_login_at;
  const fmtTs = (ts) => fmtDateTime(ts, "Never");
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [sendInvite, setSendInvite] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/companies/${companyId}/users`).then((r) => setUsers(r.data)).catch(() => {});
    if (isMaster) api.get(`/companies/${companyId}/login-history?limit=100`).then((r) => setLoginHist(r.data)).catch(() => setLoginHist(null));
  }, [companyId, isMaster]);
  useEffect(() => { load(); }, [load]);
  const { sorted, sort, toggle } = useSort(users, CU_A, "name", "asc");

  const setRole = async (uid, client_role) => {
    try {
      await api.put(`/companies/${companyId}/users/${uid}/role`, { client_role });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const preview = async (uid, name) => {
    try {
      const { data } = await api.post(`/admin/impersonate/${uid}`);
      localStorage.setItem("impersonation_token", data.token);
      toast.success(`Previewing ${name}'s portal`);
      window.location.href = "/client";
    } catch (e) { toast.error(apiErr(e)); }
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/users`, {
        name: form.name, email: form.email, company_id: companyId,
        send_invite: sendInvite,
        password: sendInvite ? undefined : form.password,
      });
      toast.success(data.email_sent ? "Invite email sent" : "Client login created");
      setOpen(false);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Revoke this client login?")) return;
    await api.delete(`/companies/${companyId}/users/${id}`);
    toast.success("Login revoked");
    load();
  };

  return (
    <div className="border border-border bg-white mt-6">
      <div className="px-5 py-4 border-b border-border flex items-center gap-2">
        <KeyRound className="h-4 w-4" />
        <h2 className="font-bold tracking-tight">Client Logins</h2>
        <span className="ml-auto text-xs font-mono text-muted-foreground">{users.length} user(s)</span>
        {isAdmin && (
          <button data-testid="add-client-user-button" onClick={() => setOpen(true)} className="ml-3 flex items-center gap-1.5 bg-black text-white px-3 py-1.5 text-xs font-medium hover:bg-neutral-800 transition-colors">
            <Plus className="h-3.5 w-3.5" /> Invite
          </button>
        )}
      </div>
      {users.length ? (<>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              <th className="px-5 py-2.5"><SortBtn label="Name" col="name" sort={sort} onSort={toggle} /></th>
              <th className="px-5 py-2.5"><SortBtn label="Role" col="role" sort={sort} onSort={toggle} /></th>
              {isMaster && <th className="px-5 py-2.5">Last login</th>}
              <th className="px-5 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((u) => (
              <tr key={u.user_id} data-testid={`client-user-${u.user_id}`} className="border-b border-border last:border-0 hover:bg-muted/50">
                <td className="px-5 py-3">
                  <div className="font-medium">{u.name}</div>
                  <div className="text-xs font-mono text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" /> {u.email}</div>
                </td>
                <td className="px-5 py-3">
                  {isMaster ? (
                    <>
                      <select
                        data-testid={`client-role-${u.user_id}`}
                        value={u.client_role || "admin"}
                        onChange={(e) => setRole(u.user_id, e.target.value)}
                        className="border border-input px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-black"
                      >
                        {CLIENT_ROLES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                      </select>
                    </>
                  ) : (
                    <span className="text-[10px] font-mono uppercase tracking-widest bg-neutral-200 text-neutral-700 px-2 py-1">
                      {CLIENT_ROLES.find((r) => r.v === (u.client_role || "admin"))?.label || "client"}
                    </span>
                  )}
                </td>
                {isMaster && <td className="px-5 py-3 text-xs font-mono tabular-nums text-muted-foreground" data-testid={`client-last-login-${u.user_id}`}>{fmtTs(lastLogin(u.user_id))}
                  {loginHist?.email_blocked?.[String(u.email).toLowerCase()] && <div data-testid={`client-email-blocked-${u.user_id}`} title={`Notifications auto-removed after ${loginHist.email_blocked[u.email.toLowerCase()].failures} delivery failures: ${loginHist.email_blocked[u.email.toLowerCase()].error}`} className="mt-1 inline-block text-[9px] font-mono uppercase tracking-widest border border-red-600 bg-red-50 text-red-700 px-1.5 py-0.5">Email blocked</div>}
                </td>}
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <ResendInviteButton u={u} onDone={load} />
                    <LinkEmployeeControl companyId={companyId} u={u} onDone={load} />
                    {isMaster && (
                      <button data-testid={`preview-portal-${u.user_id}`} onClick={() => preview(u.user_id, u.name)} title="Preview this client's portal" className="h-8 inline-flex items-center gap-1.5 border border-border px-2.5 text-xs hover:border-black hover:bg-black hover:text-white transition-colors">
                        <Eye className="h-3.5 w-3.5" /> Preview portal
                      </button>
                    )}
                    {isAdmin && (
                      <button data-testid={`revoke-client-${u.user_id}`} onClick={() => remove(u.user_id)} className="h-8 w-8 inline-flex items-center justify-center border border-border hover:border-red-600 hover:bg-red-600 hover:text-white transition-colors">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {isMaster && loginHist && (
          <div className="border-t border-border" data-testid="client-login-history">
            <button data-testid="client-login-history-toggle" onClick={() => setHistOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-2.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-black transition-colors">
              <span>Login history · {loginHist.history.length} sign-in(s) <span className="ml-2 border border-black px-1 py-0.5">Master admin</span></span><span>{histOpen ? "Hide" : "Show"}</span>
            </button>
            {histOpen && (loginHist.history.length ? (
              <table className="w-full text-xs">
                <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-t border-border bg-neutral-50"><th className="px-5 py-2">When</th><th className="px-5 py-2">User</th><th className="px-5 py-2">Method</th><th className="px-5 py-2">IP</th><th className="px-5 py-2">Browser</th></tr></thead>
                <tbody>{loginHist.history.map((h, i) => (
                  <tr key={i} className="border-t border-border/60" data-testid="client-login-history-row">
                    <td className="px-5 py-1.5 font-mono tabular-nums">{fmtDateTime(h.ts)}</td>
                    <td className="px-5 py-1.5">{h.email}</td><td className="px-5 py-1.5 uppercase">{h.method}</td>
                    <td className="px-5 py-1.5 font-mono">{h.ip}</td><td className="px-5 py-1.5 text-muted-foreground truncate max-w-[280px]" title={h.user_agent}>{h.user_agent}</td>
                  </tr>))}</tbody>
              </table>
            ) : <div className="px-5 py-4 text-xs text-muted-foreground">No sign-ins recorded yet — history starts from today's release.</div>)}
          </div>
        )}
      </>
      ) : (
        <div className="px-5 py-10 text-center text-sm text-muted-foreground">
          No client logins yet. {isAdmin ? "Invite a client to give them access to this company's payroll only." : ""}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-black w-[95vw] sm:max-w-xl max-h-[94vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="tracking-tight">Invite Client User</DialogTitle>
            <DialogDescription className="text-xs">This login can only access this company's payroll data.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {["name", "email"].map((k) => (
              <div key={k}>
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{k === "name" ? "Full name" : k}</label>
                <input
                  data-testid={`client-${k}-input`}
                  type={k === "email" ? "email" : "text"}
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>
            ))}
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input data-testid="client-send-invite-toggle" type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} className="h-4 w-4 accent-black" />
              Email a secure "set your password" link
            </label>
            {!sendInvite && (
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Initial password</label>
                <PasswordField testId="client-password-input" meter value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <button data-testid="cancel-client-user-button" className="w-full sm:w-auto border border-border px-6 py-3 text-sm font-medium hover:border-black transition-colors">Cancel</button>
            </DialogClose>
            <button data-testid="save-client-user-button" disabled={busy || !form.email || (!sendInvite && !form.password)} onClick={create} className="w-full sm:w-auto sm:px-10 bg-black text-white py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
              {busy ? "Working…" : sendInvite ? "Send invite" : "Create login"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
